import { PyMuiIconButton, PyMuiMenuItem, PyMuiMenu } from '../components/generalComponents';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import { TOOLBAR_COLORS } from '../helpers/toolbarStyleSettings';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import type { MouseEvent } from 'react';
import { useState, useEffect } from 'react';
import { useActiveEditor } from '../ActiveEditorContext';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { selectTextDefaultStyles } from '../../../grid/reduxStore/textDefaultStylingSlice.ts';
import { getParentAttribute } from '../helpers/textStyleHelpers.ts';
import type { Editor, EditorEvents } from '@tiptap/react';

type AlignmentOption = {
  testId: string;
  alignment: string;
  Icon: React.ComponentType<any>;
  onClick: () => void;
  isActive: boolean;
};
const STYLE_ATTRIBUTE = 'textAlign';

type AlignmentIconProps = {
  currentAlignment: string;
  disabled?: boolean;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  outlinedColorWhenActive?: string;
  'data-testid'?: string;
};

const AlignmentIcon = ({ currentAlignment, ...props }: AlignmentIconProps) => {
  const getIcon = () => {
    switch (currentAlignment) {
      case 'center':
        return <FormatAlignCenterIcon />;
      case 'right':
        return <FormatAlignRightIcon />;
      case 'justify':
        return <FormatAlignJustifyIcon />;
      case 'left':
      default:
        return <FormatAlignLeftOutlinedIcon />;
    }
  };

  return (
    <PyMuiIconButton disabled={props.disabled} attributeName="textAlign" outlinedColorWhenActive={TOOLBAR_COLORS.primary} {...props}>
      {getIcon()}
    </PyMuiIconButton>
  );
};

const AlignLeftIcon = ({ ...props }) => {
  return (
    <PyMuiIconButton {...props}>
      <FormatAlignLeftIcon />
    </PyMuiIconButton>
  );
};

const AlignCenterIcon = ({ ...props }) => {
  return (
    <PyMuiIconButton {...props}>
      <FormatAlignCenterIcon />
    </PyMuiIconButton>
  );
};

const AlignRightIcon = ({ ...props }) => {
  return (
    <PyMuiIconButton {...props}>
      <FormatAlignRightIcon />
    </PyMuiIconButton>
  );
};

const AlignJustifyIcon = ({ ...props }) => {
  return (
    <PyMuiIconButton {...props}>
      <FormatAlignJustifyIcon />
    </PyMuiIconButton>
  );
};

const TextAlignment = ({ disabled = false }: { disabled?: boolean }) => {
  const { activeEditor: editor } = useActiveEditor();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [spotAlignment, setSpotAlignment] = useState<string | null>(null);
  const [currentAlignment, setCurrentAlignment] = useState<string>('left');
  const { activeEditor } = useActiveEditor();
  const textDefaultStyles = useAppSelector(selectTextDefaultStyles);

  useEffect(() => {
    if (!editor) return;

    const updateAlignmentFromEditor = (currentEditor: Editor) => {
      const parent = currentEditor.state.selection.$from.parent;
      let newAlignment = 'left';

      if (parent?.attrs[STYLE_ATTRIBUTE]) {
        const alignment = parent.attrs[STYLE_ATTRIBUTE];
        if (currentEditor.isActive({ [STYLE_ATTRIBUTE]: alignment })) {
          newAlignment = alignment;
        }
      } else {
        const parentValue = getParentAttribute(parent, STYLE_ATTRIBUTE, textDefaultStyles);
        newAlignment = parentValue || 'left';
      }

      setCurrentAlignment(newAlignment);

      if (spotAlignment && spotAlignment !== newAlignment) {
        setSpotAlignment(null);
      }
    };

    const updateAlignment = ({ editor }: EditorEvents['transaction'] | EditorEvents['selectionUpdate']) => {
      updateAlignmentFromEditor(editor);
    };

    editor.on('transaction', updateAlignment);
    editor.on('selectionUpdate', updateAlignment);

    updateAlignmentFromEditor(editor);

    return () => {
      editor.off('transaction', updateAlignment);
      editor.off('selectionUpdate', updateAlignment);
    };
  }, [editor, textDefaultStyles, spotAlignment]);

  const getActiveAlignment = (currentValue: string): boolean => {
    if (!editor) return false;

    const parent = editor.state.selection.$from.parent;
    const parentValue = getParentAttribute(parent, STYLE_ATTRIBUTE, textDefaultStyles);

    if (spotAlignment) return spotAlignment === currentValue;
    if (parent?.attrs[STYLE_ATTRIBUTE]) return editor.isActive({ [STYLE_ATTRIBUTE]: currentValue }) ?? false;
    return parentValue === currentValue;
  };

  const handleIconClick = (alignment: string) => {
    activeEditor?.chain().focus().setTextAlign(alignment).run();
    setSpotAlignment(alignment);
    setCurrentAlignment(alignment);
  };

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const alignmentOptions: AlignmentOption[] = [
    {
      testId: 'align-left',
      alignment: 'left',
      Icon: AlignLeftIcon,
      onClick: () => handleIconClick('left'),
      isActive: getActiveAlignment('left') ?? false,
    },
    {
      testId: 'align-center',
      alignment: 'center',
      Icon: AlignCenterIcon,
      onClick: () => handleIconClick('center'),
      isActive: getActiveAlignment('center') ?? false,
    },
    {
      testId: 'align-right',
      alignment: 'right',
      Icon: AlignRightIcon,
      onClick: () => handleIconClick('right'),
      isActive: getActiveAlignment('right') ?? false,
    },
    {
      testId: 'align-justify',
      alignment: 'justify',
      Icon: AlignJustifyIcon,
      onClick: () => handleIconClick('justify'),
      isActive: getActiveAlignment('justify') ?? false,
    },
  ];

  return (
    <>
      <AlignmentIcon
        currentAlignment={currentAlignment}
        disabled={disabled}
        data-testid="toolbar-textAlign-button"
        onClick={handleOpenMenu}
        outlinedColorWhenActive={TOOLBAR_COLORS.primary}
      />

      <PyMuiMenu
        testId="alignment-menu"
        id="select-alignment-menu"
        menuListProps="select-alignment-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleCloseMenu}
        direction="row"
      >
        {alignmentOptions.map(({ testId, Icon, onClick, isActive }) => (
          <PyMuiMenuItem key={testId} testId={testId} menuItem={<Icon onClick={onClick} isActive={isActive} attributeName={testId} />} />
        ))}
      </PyMuiMenu>
    </>
  );
};

export default TextAlignment;
